import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CustomerService } from '../../../../customer.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { MasterManagementService } from '../../../../../management/master-management/master-management.service';
import { NzIconService } from 'ng-zorro-antd/icon';
import { map } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ApplicationContextService } from '../../../../../../shared/services/application-context.service';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-customer-capability',
  templateUrl: './customer-capability.component.html',
  styleUrl: './customer-capability.component.scss'
})
export class CustomerCapabilityComponent implements OnInit {
  applications: any[] = [];
  selectedApplicationId: number | null = null;
  selectedApplication: any | null = null; // Store selected application object

  capabilities: any[] = [];
  selectedCapabilitiesByApp: Record<number, Set<number>> = {}; // Store selected capability IDs per application
  selectedCapabilitiesByName: Set<string> = new Set<string>(); // Store selected capability names for storage/transport

  isSaving: boolean = false;
  searchText: string = '';
  isLoading: boolean = false;
  isLoadingCapabilities: boolean = false;
  customer_id: string;
  customerConfig: any[] = [];
  // Edit modal properties
  isEditModalVisible: boolean = false;
  editingCapability: any | null = null;
  editForm!: FormGroup;
  isSavingEdit: boolean = false;

  constructor(
    private fb: FormBuilder,
    private customerService: CustomerService,
    private route: ActivatedRoute,
    private masterManagementService: MasterManagementService,
    private commonService: CommonService,
    private iconService: NzIconService,
    private applicationContextService: ApplicationContextService
  ) {

    this.customer_id = this.route.snapshot.params['id'];

    this.applications = [];
    this.selectedApplicationId = null;
    this.isSaving = false;
    this.searchText = '';
    this.isLoading = false;
  }

  ngOnInit(): void {
    this.subscribeToApplicationContext();
    this.getApplicationsByCustomerId();
    this.getMasterCapabilities();
  }

  subscribeToApplicationContext() {
    this.applicationContextService.selectedApplicationId$.pipe(untilDestroyed(this)).subscribe(applicationId => {
      const previousApplicationId = this.selectedApplicationId;
      this.selectedApplicationId = applicationId;

      console.log('Customer Capability - Application changed to:', applicationId);

      // Update selected application and load data
      this.updateSelectedApplicationAndLoadData(applicationId);
    });
  }

  updateSelectedApplicationAndLoadData(applicationId: number | null) {
    if (applicationId) {
      // Find the selected application from available applications
      this.selectedApplication = this.applications.find(app => app.id === applicationId) || null;

      // If we found the application or if applications haven't loaded yet, load data
      if (this.selectedApplication || this.applications.length === 0) {
        this.getCustomerConfig();
      }

      // If applications array is empty, we need to wait for it to load
      if (this.applications.length === 0) {
        console.log('Applications not loaded yet, will load data after applications are fetched');
      }
    }
  }

  getCustomerConfig() {
    if (!this.selectedApplicationId) {
      console.log('No application selected, skipping getCustomerConfig');
      return;
    }

    // For storage/transport applications, use feature_id 100 and specific app_id
    let app_id = this.selectedApplicationId;
    let feature_id: number | undefined = undefined;

    // Find application by ID if selectedApplication is not set
    if (!this.selectedApplication && this.applications.length > 0) {
      this.selectedApplication = this.applications.find(app => app.id === this.selectedApplicationId) || null;
    }

    if (this.selectedApplication) {
      const appName = this.selectedApplication.name?.toLowerCase();
      if (appName === 'storage') {
        app_id = 1;
        feature_id = 100;
      } else if (appName === 'transport') {
        app_id = 2;
        feature_id = 100;
      }
    }

    console.log('Loading customer config for application:', app_id, 'feature:', feature_id);

    const params: any = {
      id: this.customer_id,
      app_id: app_id
    };

    if (feature_id !== undefined) {
      params.feature_id = feature_id;
    }

    this.isLoading = true;
    console.log('Calling getCustomerConfig with params:', params);
    this.customerService.getCustomerConfig(params).subscribe((res: any) => {
      console.log('getCustomerConfig response:', res);
      if (res.status === 200) {
        this.customerConfig = res.data;

        // For storage/transport, check capabilities by name
        if (feature_id === 100 && (app_id === 1 || app_id === 2)) {
          this.selectedCapabilitiesByName.clear();
          const configData = Array.isArray(this.customerConfig) ? this.customerConfig : [this.customerConfig];

          configData.forEach((config: any) => {
            // Handle value key with JSON string
            if (config && config.value) {
              try {
                // Parse the JSON string from value key
                const parsedValue = typeof config.value === 'string'
                  ? JSON.parse(config.value)
                  : config.value;

                // Handle parsed value as array
                const capabilitiesArray = Array.isArray(parsedValue) ? parsedValue : [parsedValue];

                capabilitiesArray.forEach((capability: any) => {
                  if (capability && capability.name) {
                    this.selectedCapabilitiesByName.add(capability.name);
                  }
                });
              } catch (e) {
                console.error('Error parsing value JSON:', e);
              }
            }

            // Also handle feature_config as array of objects (fallback)
            if (config && config.feature_config && Array.isArray(config.feature_config)) {
              config.feature_config.forEach((capability: any) => {
                if (capability && capability.name) {
                  this.selectedCapabilitiesByName.add(capability.name);
                }
              });
            }
          });
        } else {
          // For other applications, use existing logic with IDs
          const configData = Array.isArray(this.customerConfig) ? this.customerConfig : [this.customerConfig];
          configData.forEach((config: any) => {
            // Handle capability as array (new structure)
            if (config && config.capability && Array.isArray(config.capability)) {
              const capabilityIds = config.capability.map((capability: any) => capability.id).filter((id: any) => id != null);
              this.selectedCapabilitiesByApp[config.application_id] = new Set<number>(capabilityIds);
            }
          });
        }
      } else {
        console.error('Failed to get customer config - status:', res.status);
        this.commonService.message.error('Failed to get customer config');
      }
      this.isLoading = false;
    }, (error: any) => {
      console.error('Error calling getCustomerConfig:', error);
      this.commonService.message.error('Error loading customer configuration');
      this.isLoading = false;
    });
  }

  getApplicationsByCustomerId() {
    if (!this.customer_id) return;
    const params = {
      filters: JSON.stringify({ customer_id: this.customer_id }),
      filterType: 'customer'
    }
    this.isLoading = true;
    this.customerService.getApplications(params).subscribe((res: any) => {
      if (res.status === 200) {
        this.applications = res.data;
        this.applications.forEach(app => {
          if (!this.selectedCapabilitiesByApp[app.id]) {
            this.selectedCapabilitiesByApp[app.id] = new Set<number>();
          }
        });

        // After applications are loaded, trigger data loading for the current application context
        const contextApplicationId = this.applicationContextService.getSelectedApplicationId();
        if (contextApplicationId) {
          console.log('Applications loaded, triggering data load for application:', contextApplicationId);
          this.updateSelectedApplicationAndLoadData(contextApplicationId);
        }
      }
      this.isLoading = false;
    }, (err: any) => {
      this.isLoading = false;
      console.error(err);
    });
  }

  getMasterCapabilities() {
    this.isLoadingCapabilities = true;
    this.masterManagementService.getCapabilities().subscribe((res: any) => {
      if (res.status === 200) {
        this.capabilities = res.data || [];
        this.applications.forEach(app => {
          if (!this.selectedCapabilitiesByApp[app.id]) {
            this.selectedCapabilitiesByApp[app.id] = new Set<number>();
          }
        });
      } else {
        this.commonService.message.error('Failed to get capabilities');
      }
      this.isLoadingCapabilities = false;
    }, (err: any) => {
      this.isLoadingCapabilities = false;
      this.commonService.message.error('Failed to get capabilities');
      console.error(err);
    });
  }

  get visibleCapabilities() {
    if (!this.searchText?.trim()) return this.capabilities;
    const q = this.searchText.trim().toLowerCase();
    return this.capabilities.filter((c: any) =>
      c.name?.toLowerCase().includes(q) ||
      c.unit?.toLowerCase().includes(q)
    );
  }

  isChecked(capabilityId: number): boolean {
    if (!this.selectedApplicationId) return false;

    // For storage/transport, check by name
    if (this.selectedApplication) {
      const appName = this.selectedApplication.name?.toLowerCase();
      if (appName === 'storage' || appName === 'transport') {
        const capability = this.capabilities.find(c => c.id === capabilityId);
        if (capability && capability.name) {
          return this.selectedCapabilitiesByName.has(capability.name);
        }
        return false;
      }
    }

    // For other applications, check by ID
    const selectedSet = this.selectedCapabilitiesByApp[this.selectedApplicationId];
    return selectedSet ? selectedSet.has(capabilityId) : false;
  }

  toggleCapability(capabilityId: number): void {
    if (!this.selectedApplicationId) return;

    const capability = this.capabilities.find(c => c.id === capabilityId);
    if (!capability) return;

    // For storage/transport, use name-based selection
    if (this.selectedApplication) {
      const appName = this.selectedApplication.name?.toLowerCase();
      if (appName === 'storage' || appName === 'transport') {
        if (capability.name) {
          if (this.selectedCapabilitiesByName.has(capability.name)) {
            this.selectedCapabilitiesByName.delete(capability.name);
          } else {
            this.selectedCapabilitiesByName.add(capability.name);
          }
        }
        return;
      }
    }

    // For other applications, use ID-based selection
    // Initialize set for this application if it doesn't exist
    if (!this.selectedCapabilitiesByApp[this.selectedApplicationId]) {
      this.selectedCapabilitiesByApp[this.selectedApplicationId] = new Set<number>();
    }

    const selectedSet = this.selectedCapabilitiesByApp[this.selectedApplicationId];
    if (selectedSet.has(capabilityId)) {
      selectedSet.delete(capabilityId);
    } else {
      selectedSet.add(capabilityId);
    }
  }

  selectAllVisible(): void {
    if (!this.selectedApplicationId) return;

    // Initialize set for this application if it doesn't exist
    if (!this.selectedCapabilitiesByApp[this.selectedApplicationId]) {
      this.selectedCapabilitiesByApp[this.selectedApplicationId] = new Set<number>();
    }

    const selectedSet = this.selectedCapabilitiesByApp[this.selectedApplicationId];
    this.visibleCapabilities.forEach((c: any) => {
      if (c.id) {
        selectedSet.add(c.id);
      }
    });
  }

  clearAll(): void {
    if (!this.selectedApplicationId) return;

    if (this.selectedCapabilitiesByApp[this.selectedApplicationId]) {
      this.selectedCapabilitiesByApp[this.selectedApplicationId].clear();
    }
  }

  // Application selection is now handled by global application context
  // onApplicationChange method removed - no longer needed

  saveSelection(): void {
    if (!this.selectedApplicationId) {
      this.commonService.message.warning('Please select an application');
      return;
    }

    // Check if this is storage/transport application
    const isStorageOrTransport = this.selectedApplication &&
      (this.selectedApplication.name?.toLowerCase() === 'storage' ||
        this.selectedApplication.name?.toLowerCase() === 'transport');

    let selectedCapabilitiesData: any[] = [];

    if (isStorageOrTransport) {
      // For storage/transport, use name-based selection
      if (this.selectedCapabilitiesByName.size === 0) {
        this.commonService.message.warning('Please select at least one capability');
        return;
      }

      // Get capabilities by name
      const selectedNames = Array.from(this.selectedCapabilitiesByName);
      selectedCapabilitiesData = this.capabilities.filter(c =>
        c.name && selectedNames.includes(c.name)
      );
    } else {
      // For other applications, use ID-based selection
      const selectedSet = this.selectedCapabilitiesByApp[this.selectedApplicationId];
      if (!selectedSet || selectedSet.size === 0) {
        this.commonService.message.warning('Please select at least one capability');
        return;
      }

      const selectedCapabilityIds = Array.from(selectedSet);
      selectedCapabilitiesData = this.capabilities.filter(c => selectedCapabilityIds.includes(c.id));
    }

    this.isSaving = true;

    // Get user_id from localStorage and convert to number
    const user_id = localStorage.getItem('user_id');
    const created_by = user_id ? parseInt(user_id, 10) : 0;

    // Build feature_config as an array of objects
    const feature_config: any[] = [];

    selectedCapabilitiesData.forEach((capability: any) => {
      // Parse metadata
      let metadata: any = {};
      try {
        metadata = typeof capability.metadata === 'string'
          ? JSON.parse(capability.metadata)
          : (capability.metadata || {});
      } catch (e) {
        metadata = {};
      }

      // Add capability object to array with all its details
      feature_config.push({
        icon: metadata?.icon || '',
        color: metadata?.color || '#1890ff',
        unit: capability.unit || '',
        id: capability.id,
        name: capability.name
      });
    });

    // Build payload - capabilities are saved per application
    const payload = {
      customer_id: this.customer_id,
      application_id: this.selectedApplicationId,
      feature_id: 100,
      created_by: created_by,
      feature_config: feature_config
    };

    // Call API to save customer config for this specific application
    this.customerService.addCustomerConfig(payload).subscribe((res: any) => {
      if (res.status === 200) {
        this.commonService.message.success('Capabilities saved successfully for this application');
        this.getCustomerConfig();
      } else {
        this.commonService.message.error(res.message || 'Failed to save capabilities');
      }
      this.isSaving = false;
    }, (err: any) => {
      this.commonService.message.error('An error occurred while saving capabilities');
      this.isSaving = false;
      console.error(err);
    });
  }

  openEditModal(capability: any): void {
    this.editingCapability = capability;
    this.initializeEditForm(capability);
    this.isEditModalVisible = true;
  }

  initializeEditForm(capability: any): void {
    // Parse metadata if it's a string, otherwise use as object
    let metadata: any = {};
    try {
      metadata = typeof capability.metadata === 'string'
        ? JSON.parse(capability.metadata)
        : (capability.metadata || {});
    } catch (e) {
      metadata = {};
    }

    this.editForm = this.fb.group({
      color: [metadata?.color || '#1890ff', [Validators.required]],
      icon: [metadata?.icon || ''],
      unit: [capability.unit || ''],
      minValue: [null], // These fields may not exist in API response
      maxValue: [null],
      threshold: [null]
    });
  }

  handleEditCancel(): void {
    this.isEditModalVisible = false;
    this.editingCapability = null;
    this.editForm.reset();
  }

  saveCapabilityEdit(): void {
    if (this.editForm.valid && this.editingCapability) {
      this.isSavingEdit = true;

      const formValue = this.editForm.value;
      const { color, icon, unit } = formValue;

      // Update metadata
      const metadata = {
        icon: icon,
        color: color
      };

      // Update the capability object locally
      this.editingCapability.metadata = metadata;
      this.editingCapability.unit = unit;

      // Simulate API call
      setTimeout(() => {
        console.log('Updated capability:', this.editingCapability);
        this.commonService.message.success('Capability updated successfully');
        this.isSavingEdit = false;
        this.isEditModalVisible = false;
        this.editingCapability = null;
        this.editForm.reset();
      }, 600);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.editForm.controls).forEach(key => {
        this.editForm.get(key)?.markAsTouched();
      });
    }
  }

  getCapabilityIconConfig(capability: any): { iconName: string; color: string } {
    let color = '#1890ff';
    let iconName = `capability:${capability.name}`;

    // Extract color from metadata
    if (capability.metadata) {
      try {
        const metadata = typeof capability.metadata === 'string'
          ? JSON.parse(capability.metadata)
          : capability.metadata;
        color = metadata?.color || '#1890ff';
      } catch (e) {
        // Use default color if parsing fails
      }
    }

    return { iconName, color };
  }
}
